/**
 * TravelPayouts affiliate provider. One marker covers flights (Aviasales),
 * hotels (Hotellook), and tours via the `tp.media` redirector. See ADR-0064.
 *
 * URL templates are derived from the partner dashboard at:
 *   https://www.travelpayouts.com/programs  →  Tools → API & deep links
 *
 * !!! VERIFY-VS-PARTNER-DASHBOARD !!!
 * The URL templates below (Hotellook query params, Aviasales DDMM path
 * format, tp.media redirector targets for tours / car rental / eSIM /
 * airport transfers / Omio rail-bus-ferry, white-label widget params)
 * have NOT been confirmed against a live partner dashboard. Vitest
 * snapshots prove they are
 * deterministic, not that they are correct. Before driving real click
 * volume, sign in to the TravelPayouts dashboard, copy a sample deep-link
 * for each product, and reconcile against the URL produced by the matching
 * Vitest snapshot. If TravelPayouts updates their parameter names, only
 * this file changes — call sites stay vendor-neutral via AffiliateProvider.
 */

import type {
  AffiliateEnv,
  AffiliateProvider,
  AffiliateWidgetSnippet,
  AirportTransferInput,
  CarRentalSearchInput,
  EsimSearchInput,
  FlightBrowseInput,
  FlightSearchInput,
  HotelSearchInput,
  ToursSearchInput,
  TrainSearchInput,
  TripWidgetInput,
} from './affiliate-provider';
import { NoopAffiliateProvider } from './affiliate-provider';

const HOTELLOOK_HOST = 'https://search.hotellook.com';
const AVIASALES_HOST = 'https://www.aviasales.com';
const TP_REDIRECT_HOST = 'https://tp.media';
const DEFAULT_WIDGET_HOST = 'https://tp.media';

const IATA_PATTERN = /^[A-Z]{3}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const COUNTRY_CODE_PATTERN = /^[A-Za-z]{2}$|^(eu|global)$/;

export interface TravelPayoutsConfig {
  marker: string;
  /** Optional sub-ID for white-label widgets (`trs` param). */
  trs?: string | undefined;
  /** Optional widget host override; defaults to tp.media. */
  widgetHost?: string | undefined;
}

export class TravelPayoutsProvider implements AffiliateProvider {
  readonly id = 'travelpayouts';
  private readonly marker: string;
  private readonly trs: string | undefined;
  private readonly widgetHost: string;

  constructor(config: TravelPayoutsConfig) {
    if (!config.marker) {
      throw new Error('TravelPayoutsProvider requires a non-empty marker');
    }
    this.marker = config.marker;
    this.trs = config.trs;
    this.widgetHost = config.widgetHost ?? DEFAULT_WIDGET_HOST;
  }

  buildHotelSearchUrl(input: HotelSearchInput): string {
    // Symmetric with the flight builder: reject malformed dates rather than
    // emit a broken affiliate URL that produces a tracked click with no
    // possible conversion. Returns '' so UI surfaces hide on falsy.
    if (!ISO_DATE_PATTERN.test(input.checkIn)) return '';
    if (!ISO_DATE_PATTERN.test(input.checkOut)) return '';
    const checkOut = sameOrEarlier(input.checkOut, input.checkIn)
      ? addDaysIso(input.checkIn, 1)
      : input.checkOut;
    const params = new URLSearchParams();
    params.set('destination', input.destinationName);
    // Hotellook supports lat/lng radius search via destinationLat /
    // destinationLng. Forward both or neither — a half-pair would
    // either be ignored or trigger partner-side validation. Fixed at
    // 5 decimals so we never leak hyper-precise coords (city-level
    // accuracy is what the user expects from a destination pin).
    if (input.destinationLat != null && input.destinationLng != null) {
      params.set('destinationLat', input.destinationLat.toFixed(5));
      params.set('destinationLng', input.destinationLng.toFixed(5));
    }
    params.set('checkIn', input.checkIn);
    params.set('checkOut', checkOut);
    if (input.adults != null) params.set('adults', String(input.adults));
    if (input.currency) params.set('currency', input.currency);
    if (input.locale) params.set('language', input.locale);
    params.set('marker', this.marker);
    return `${HOTELLOOK_HOST}/?${params.toString()}`;
  }

  buildFlightSearchUrl(input: FlightSearchInput): string | null {
    const origin = input.originIata?.toUpperCase();
    const dest = input.destinationIata?.toUpperCase();
    if (!origin || !dest || !IATA_PATTERN.test(origin) || !IATA_PATTERN.test(dest)) {
      return null;
    }
    if (!ISO_DATE_PATTERN.test(input.departDate)) {
      return null;
    }
    const depart = isoDateToDdmm(input.departDate);
    const ret = input.returnDate && ISO_DATE_PATTERN.test(input.returnDate)
      ? isoDateToDdmm(input.returnDate)
      : '';
    const adults = clampAdults(input.adults ?? 1);
    // Aviasales path format: /search/{ORIGIN}{DDMM}{DEST}{RETURN_DDMM?}{ADULTS}
    const path = `${origin}${depart}${dest}${ret}${adults}`;
    const params = new URLSearchParams();
    params.set('marker', this.marker);
    if (input.currency) params.set('currency', input.currency);
    if (input.locale) params.set('locale', input.locale);
    return `${AVIASALES_HOST}/search/${path}?${params.toString()}`;
  }

  buildFlightBrowseUrl(input: FlightBrowseInput): string {
    // Marker-tagged Aviasales homepage for surfaces that can't supply
    // an IATA pair (e.g. add-choice opened from a mid-trip ghost). The
    // partner search form is the landing page; the marker attributes
    // any subsequent search the user runs.
    const params = new URLSearchParams();
    params.set('marker', this.marker);
    if (input.currency) params.set('currency', input.currency);
    if (input.locale) params.set('locale', input.locale);
    return `${AVIASALES_HOST}/?${params.toString()}`;
  }

  buildToursSearchUrl(input: ToursSearchInput): string {
    // Tours are routed through the tp.media redirector wrapping a Viator/Tiqets URL.
    // The target is a search page on the destination's name; the redirector
    // appends marker + trs and returns a 302 to the underlying partner.
    // When an interest keyword is provided we concatenate it into `q` so
    // the partner's full-text search pre-filters (e.g. "Tokyo food").
    const targetParams = new URLSearchParams();
    const keyword = input.interest
      ? `${input.destinationName} ${input.interest}`.trim()
      : input.destinationName;
    targetParams.set('q', keyword);
    if (input.date) targetParams.set('date', input.date);
    if (input.locale) targetParams.set('locale', input.locale);
    const target = `https://www.viator.com/searchResults/all?${targetParams.toString()}`;
    const params = new URLSearchParams();
    params.set('marker', this.marker);
    params.set('u', target);
    if (this.trs) params.set('trs', this.trs);
    return `${TP_REDIRECT_HOST}/r?${params.toString()}`;
  }

  buildCarRentalSearchUrl(input: CarRentalSearchInput): string {
    // Car rental is routed through the tp.media redirector wrapping a
    // Discover Cars search URL. Symmetric date validation with the hotel
    // builder: reject malformed pickup/dropoff and bump dropoff by one day
    // when the user passed the same date for both (or dropoff <= pickup).
    if (!ISO_DATE_PATTERN.test(input.pickupDate)) return '';
    if (!ISO_DATE_PATTERN.test(input.dropoffDate)) return '';
    const dropoffDate = sameOrEarlier(input.dropoffDate, input.pickupDate)
      ? addDaysIso(input.pickupDate, 1)
      : input.dropoffDate;
    const targetParams = new URLSearchParams();
    targetParams.set('pickup_location', input.pickupLocation);
    targetParams.set('dropoff_location', input.dropoffLocation ?? input.pickupLocation);
    targetParams.set('pickup_date', input.pickupDate);
    targetParams.set('dropoff_date', dropoffDate);
    if (input.pickupTime && TIME_PATTERN.test(input.pickupTime)) {
      targetParams.set('pickup_time', input.pickupTime);
    }
    if (input.dropoffTime && TIME_PATTERN.test(input.dropoffTime)) {
      targetParams.set('dropoff_time', input.dropoffTime);
    }
    if (input.currency) targetParams.set('currency', input.currency);
    if (input.locale) targetParams.set('locale', input.locale);
    const target = `https://www.discovercars.com/?${targetParams.toString()}`;
    const params = new URLSearchParams();
    params.set('marker', this.marker);
    params.set('u', target);
    if (this.trs) params.set('trs', this.trs);
    return `${TP_REDIRECT_HOST}/r?${params.toString()}`;
  }

  buildEsimSearchUrl(input: EsimSearchInput): string {
    // eSIM is routed through the tp.media redirector wrapping an Airalo
    // local-plan page. Country code is the only required input; the
    // partner page handles plan selection. Free-form country names are
    // accepted as a fallback query keyword.
    if (!COUNTRY_CODE_PATTERN.test(input.countryCode)) return '';
    const cc = input.countryCode.toLowerCase();
    const targetParams = new URLSearchParams();
    targetParams.set('country', cc);
    if (input.countryName) targetParams.set('q', input.countryName);
    if (input.locale) targetParams.set('locale', input.locale);
    const target = `https://www.airalo.com/${cc}-esim?${targetParams.toString()}`;
    const params = new URLSearchParams();
    params.set('marker', this.marker);
    params.set('u', target);
    if (this.trs) params.set('trs', this.trs);
    return `${TP_REDIRECT_HOST}/r?${params.toString()}`;
  }

  buildAirportTransferUrl(input: AirportTransferInput): string {
    // Airport transfer is routed through the tp.media redirector wrapping
    // a Kiwi Taxi search. Origin + destination + date are required;
    // time/passengers are optional and forwarded when valid.
    if (!ISO_DATE_PATTERN.test(input.date)) return '';
    if (!input.originName || !input.destinationName) return '';
    const targetParams = new URLSearchParams();
    targetParams.set('from', input.originName);
    targetParams.set('to', input.destinationName);
    targetParams.set('date', input.date);
    if (input.time && TIME_PATTERN.test(input.time)) {
      targetParams.set('time', input.time);
    }
    if (input.passengers != null) {
      targetParams.set('passengers', String(clampPassengers(input.passengers)));
    }
    if (input.currency) targetParams.set('currency', input.currency);
    if (input.locale) targetParams.set('locale', input.locale);
    const target = `https://kiwitaxi.com/search?${targetParams.toString()}`;
    const params = new URLSearchParams();
    params.set('marker', this.marker);
    params.set('u', target);
    if (this.trs) params.set('trs', this.trs);
    return `${TP_REDIRECT_HOST}/r?${params.toString()}`;
  }

  buildTrainSearchUrl(input: TrainSearchInput): string {
    // Rail / bus / ferry is routed through the tp.media redirector
    // wrapping an Omio search. Origin + destination + depart date
    // are required; time/passengers/mode are optional and forwarded
    // when valid. The mode hint preselects the partner tab; when
    // omitted, Omio defaults to its combined results view.
    if (!ISO_DATE_PATTERN.test(input.departDate)) return '';
    if (!input.originName || !input.destinationName) return '';
    const targetParams = new URLSearchParams();
    targetParams.set('from', input.originName);
    targetParams.set('to', input.destinationName);
    targetParams.set('date', input.departDate);
    if (input.departTime && TIME_PATTERN.test(input.departTime)) {
      targetParams.set('time', input.departTime);
    }
    if (input.passengers != null) {
      targetParams.set('passengers', String(clampAdults(input.passengers)));
    }
    if (input.mode) targetParams.set('mode', input.mode);
    if (input.currency) targetParams.set('currency', input.currency);
    if (input.locale) targetParams.set('locale', input.locale);
    const target = `https://www.omio.com/search-frontend/results?${targetParams.toString()}`;
    const params = new URLSearchParams();
    params.set('marker', this.marker);
    params.set('u', target);
    if (this.trs) params.set('trs', this.trs);
    return `${TP_REDIRECT_HOST}/r?${params.toString()}`;
  }

  buildTripWidget(input: TripWidgetInput): AffiliateWidgetSnippet {
    const params = new URLSearchParams();
    params.set('shmarker', this.marker);
    if (this.trs) params.set('trs', this.trs);
    params.set('destination', input.destinationName);
    if (input.checkIn) params.set('checkIn', input.checkIn);
    if (input.checkOut) params.set('checkOut', input.checkOut);
    if (input.currency) params.set('currency', input.currency);
    if (input.locale) params.set('locale', input.locale);
    const url = `${this.widgetHost}/content?${params.toString()}`;
    const html = `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body,iframe{margin:0;padding:0;border:0;width:100%;height:100%}</style></head><body><iframe src="${escapeAmpersand(url)}" sandbox="allow-scripts allow-same-origin allow-popups allow-forms" referrerpolicy="no-referrer-when-downgrade"></iframe></body></html>`;
    return { html, url };
  }
}

/**
 * Factory keyed on env. Returns a Noop when the marker is missing — every
 * UI surface is expected to hide itself when `provider.id === 'noop'`.
 */
export function createAffiliateProvider(env: AffiliateEnv): AffiliateProvider {
  if (!env.travelpayoutsMarker) {
    return new NoopAffiliateProvider();
  }
  return new TravelPayoutsProvider({
    marker: env.travelpayoutsMarker,
    trs: env.travelpayoutsTrs,
    widgetHost: env.travelpayoutsHost,
  });
}

// ---------------------------------------------------------------------------
// Pure date helpers (kept local — no external date lib needed for yyyy-mm-dd).
// ---------------------------------------------------------------------------

function isoDateToDdmm(iso: string): string {
  // iso is yyyy-mm-dd; Aviasales path format wants ddmm without the year.
  const day = iso.slice(8, 10);
  const month = iso.slice(5, 7);
  return `${day}${month}`;
}

function sameOrEarlier(maybe: string, anchor: string): boolean {
  if (!ISO_DATE_PATTERN.test(maybe) || !ISO_DATE_PATTERN.test(anchor)) return false;
  return maybe <= anchor;
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  // UTC arithmetic to avoid host-timezone edge cases when crossing DST.
  const next = new Date(Date.UTC(y, m - 1, d + days));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// Aviasales path format is `{ORIGIN}{DDMM}{DEST}{RETURN_DDMM?}{ADULTS}`
// — adults is a single-digit slot, glued directly after the destination
// IATA. If adults were two digits (10+), the parser would read the leading
// digit as part of the IATA and produce a broken search. The clamp at 9
// is load-bearing for URL correctness, not just a cosmetic limit. A family
// of 10+ will silently search for 9 — accepted trade-off for the few
// users this affects.
function clampAdults(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(9, Math.floor(n)));
}

// Transfer partners typically cap at 8 passengers per booking (van class).
// Unlike Aviasales adults, this isn't load-bearing for URL parsing — it
// just keeps us inside the partner's accepted range so the search page
// doesn't reject the deep-link.
function clampPassengers(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(8, Math.floor(n)));
}

// URLSearchParams already percent-encodes `"`, `<`, `>` inside values,
// so the only character that needs HTML escaping in the iframe `src=`
// attribute is the `&` separating query params. Defense in depth for
// any future param that might not survive URLSearchParams unchanged.
function escapeAmpersand(value: string): string {
  return value.replace(/&/g, '&amp;');
}
